import { useRef } from "react";
import * as api from "../api/client";
import { ItemSprite } from "./ItemSprite";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { useModalFocus } from "../logic/useModalFocus";

interface Props {
  slots: api.InventorySlot[];
  items: api.ItemDefinition[];
  cookableIds: Set<string>;
  onPick: (itemId: string) => void;
  onClose: () => void;
}

const INV_COLS = 5;
const INV_ROWS = 4;

// Inventory picker modal for the cooking page. A 5×4 grid mirroring
// the player's 20-slot inventory. Cells that aren't valid recipe
// inputs are greyed out and unclickable; empty cells are placeholders
// so the grid always reads as a real bag.
export function CookInventoryModal({
  slots,
  items,
  cookableIds,
  onPick,
  onClose,
}: Props) {
  const theme = pixelTheme;
  const itemById = new Map(items.map((i) => [i.id, i]));
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(dialogRef, onClose);

  const cells = Array.from({ length: INV_COLS * INV_ROWS }, (_, i) => {
    const s = slots[i];
    if (!s || s.item_id == null || s.count <= 0) return null;
    return s;
  });

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Choose ingredient"
      tabIndex={-1}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(24, 20, 37, 0.72)",
        display: "grid",
        placeItems: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 540,
          background: E32.parchment,
          border: `2px solid ${E32.ink}`,
          color: theme.ink,
          fontFamily: theme.fontBody,
          padding: 18,
          display: "grid",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 4,
          }}
        >
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 14,
              letterSpacing: "0.08em",
              color: theme.accent,
              textTransform: "uppercase",
            }}
          >
            Choose Ingredient
          </div>
          <button
            onClick={onClose}
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              letterSpacing: "0.08em",
              padding: "4px 8px",
              background: "transparent",
              color: theme.ink,
              border: `2px solid ${theme.ink}`,
              cursor: "pointer",
            }}
          >
            CLOSE ✕
          </button>
        </div>

        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            letterSpacing: "0.1em",
            color: theme.inkFaint,
            textTransform: "uppercase",
          }}
        >
          Click an ingredient to place it in the oven slot.
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${INV_COLS}, 1fr)`,
            gridAutoRows: 84,
            gap: 6,
          }}
        >
          {cells.map((cell, i) => {
            if (!cell || !cell.item_id) {
              return (
                <div
                  key={`empty-${i}`}
                  style={{
                    background: "rgba(0,0,0,0.04)",
                    border: `2px solid ${theme.panelEdge}`,
                  }}
                />
              );
            }

            const def = itemById.get(cell.item_id);
            const cookable = cookableIds.has(cell.item_id);
            const title = cookable
              ? def?.name ?? cell.item_id
              : `${def?.name ?? cell.item_id} — can't be cooked`;

            return (
              <button
                key={`slot-${cell.slot_index}`}
                type="button"
                data-testid={`cook-inv-cell-${cell.item_id}`}
                disabled={!cookable}
                title={title}
                onClick={() => {
                  if (cookable && cell.item_id) onPick(cell.item_id);
                }}
                style={{
                  position: "relative",
                  // Match the design's panel-flat background, no beige
                  // chip behind each item.
                  background: theme.panelSolid,
                  border: `2px solid ${theme.panelEdge}`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  padding: 6,
                  cursor: cookable ? "pointer" : "not-allowed",
                  opacity: cookable ? 1 : 0.35,
                }}
              >
                <ItemSprite id={cell.item_id} size={36} />
                <div
                  style={{
                    fontFamily: theme.fontDisplay,
                    fontSize: 7,
                    letterSpacing: "0.06em",
                    color: theme.inkDim,
                    textAlign: "center",
                    lineHeight: 1.2,
                    textTransform: "uppercase",
                  }}
                >
                  {def?.name ?? cell.item_id}
                </div>
                <div
                  style={{
                    position: "absolute",
                    top: 2,
                    right: 4,
                    fontFamily: theme.fontDisplay,
                    fontSize: 9,
                    color: theme.ink,
                    background: E32.butter,
                    border: `1px solid ${E32.ink}`,
                    padding: "1px 3px",
                  }}
                >
                  {cell.count}
                </div>
              </button>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 6,
            gap: 8,
          }}
        >
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 8,
              letterSpacing: "0.1em",
              color: theme.inkFaint,
            }}
          >
            GREYED OUT · NOT A RECIPE INGREDIENT
          </div>
        </div>
      </div>
    </div>
  );
}
